import type { OnInit } from '@angular/core';
import { Component, ChangeDetectionStrategy, inject } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { type DenormalizedSpecificBlockEntity, type WebLegacyWidgetEntity } from '@web/types';

import { BaseBlockComponent } from '@web/layout/content/page-content/components/blocks/base-block.abstract';
import { ShopCartIconViewComponent } from '@web/widgets/shop-cart-icon/components/shop-cart-icon-view/shop-cart-icon-view.component';
import { ShopCartIconFacade } from '@web/store/shop-cart-icon/shop-cart-icon.facade';
import type { BlockSpecificForm } from '@sites/entity-signal-form';
import {
    backgroundLayerVar,
    backgroundVar,
    flexAlignVar,
    responsiveSpacingSideVar,
    specificBlockBarBgVar,
    textAlignVar,
    toUnsharedBlockMeta,
} from '@sites/entity-core';
import { themeColorCss } from '@sites/site-theme/src/utils/variables';
import { environment } from '@web-env/environment';
import { ESize } from '@common/enums';
import { computed } from '@angular/core';

/** `ESize` — the burger glyph and the round box around it. */
const CART_ICON_SIZE: Record<string, string> = { S: '19px', M: '24px', L: '29px' };
const CART_ICON_BOX_SIZE: Record<string, string> = { S: '40px', M: '48px', L: '56px' };
import { DeleteSharedBlockModalComponent } from '@web/shared/modals/delete-shared-block-modal/delete-shared-block-modal.component';

import { ESelectedEntityType } from '@common/types/layout.type';
import { Widgets } from '@common/enums';
import { SingleHoverDirective } from '@web/layout/content/page-content/directives/single-hover.directive';
import { AsyncPipe } from '@angular/common';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SpecificSectionComponent } from '@web/layout/content/page-content/components/sections/specific-section/specific-section.component';
import { SpecificWidgetComponent } from '@web/layout/content/page-content/components/widgets/specific-widget/specific-widget.component';
import { AddButtonsComponent } from '@web/layout/content/page-content/components/add-buttons/add-buttons.component';
import { SpecificBlockControlsComponent } from '@web/layout/content/page-content/components/controls/specific-block-controls/specific-block-controls.component';
import { TranslateModule } from '@ngx-translate/core';
import { CSS_RESOLVERS } from '@web/core/constants/css-resolvers.const';

@Component({
    selector: 'web-specific-block',
    templateUrl: './specific-block.component.html',
    styleUrls: ['./specific-block.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SingleHoverDirective,
        TooltipModule,
        ShopCartIconViewComponent,
        SpecificSectionComponent,
        SpecificWidgetComponent,
        AddButtonsComponent,
        SpecificBlockControlsComponent,
        AsyncPipe,
        TranslateModule,
    ],
})
export class SpecificBlockComponent extends BaseBlockComponent<DenormalizedSpecificBlockEntity> implements OnInit {
    private readonly bsModalService: BsModalService = inject(BsModalService);
    private readonly shopCartIconFacade = inject(ShopCartIconFacade);

    public readonly isEcommerceMode$ = this.appFacade.isEcommerceMode$;
    public readonly iconPositionType$ = this.shopCartIconFacade.iconPositionType$;

    public readonly ESize = ESize;
    /**
     * Styling is read from the signal form rather than the entity input, the way
     * `default-column` does it. Each settings group is its own signal, so editing
     * a padding no longer recomputes the background.
     */
    private get headerForm(): BlockSpecificForm {
        return this.form as BlockSpecificForm;
    }

    // One signal per settings group, each a single FieldTree node.
    private readonly paddingValue = computed(() => this.headerForm?.padding().value());
    private readonly backgroundValue = computed(() => this.headerForm?.background().value());
    private readonly iconValue = computed(() => this.headerForm?.icon().value() as Record<string, string>);

    // Each gating class is its own signal and its own `[class.x]` binding, for
    // the same reason the custom properties are: toggling one must not re-diff
    // the rest. The editor binds neither pin — a pinned header has to stay in the
    // flow so it can still be selected.
    public readonly collapsesOnMobile = computed<boolean>(() =>
        Boolean(this.headerForm?.hideContentOnMobile().value())
    );

    public readonly hasIconRight = computed<boolean>(() => this.iconValue()?.horizontalPosition === 'R');

    public readonly iconSize = computed<ESize>(() => this.iconValue()?.size as ESize);

    // One custom property per signal.
    public readonly bg = computed(() => backgroundVar(this.backgroundValue() as never, CSS_RESOLVERS));
    public readonly bgPosition = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'position'));
    public readonly bgRepeat = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'repeat'));
    public readonly bgSize = computed(() => backgroundLayerVar(this.backgroundValue() as never, 'size'));
    public readonly barBg = computed(() => specificBlockBarBgVar(this.backgroundValue() as never, CSS_RESOLVERS));

    public readonly paddingTop = computed(() => responsiveSpacingSideVar(this.paddingValue() as never, 'top'));
    public readonly paddingBottom = computed(() => responsiveSpacingSideVar(this.paddingValue() as never, 'bottom'));
    public readonly paddingTopMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'top', 'mobile')
    );
    public readonly paddingRightMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'right', 'mobile')
    );
    public readonly paddingBottomMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'bottom', 'mobile')
    );
    public readonly paddingLeftMobile = computed(() =>
        responsiveSpacingSideVar(this.paddingValue() as never, 'left', 'mobile')
    );

    public readonly iconColor = computed(() => themeColorCss(this.iconValue()?.color));
    public readonly iconGlyphSize = computed(() => CART_ICON_SIZE[this.iconValue()?.size] ?? CART_ICON_SIZE.M);
    public readonly iconBoxSize = computed(() => CART_ICON_BOX_SIZE[this.iconValue()?.size] ?? CART_ICON_BOX_SIZE.M);
    public readonly iconGapRight = computed(() => (this.iconValue()?.horizontalPosition === 'R' ? '0' : '10px'));
    public readonly iconGapLeft = computed(() => (this.iconValue()?.horizontalPosition === 'R' ? '10px' : '0'));

    public readonly contentAlign = computed(() =>
        flexAlignVar(this.headerForm?.horizontalPosition().value(), 'flex-start')
    );
    public readonly contentTextAlign = computed(() =>
        textAlignVar(this.headerForm?.horizontalPosition().value(), 'left')
    );
    public readonly textColor = computed(() => themeColorCss(this.headerForm?.textColor().value()));

    /** Whether the header folds into the burger bar on a narrow screen. */
    public readonly hideContentOnMobile = computed<boolean>(() =>
        Boolean(this.headerForm?.hideContentOnMobile().value())
    );

    public selectBlock(): void {
        this.layoutFacade.setSelectedEntity({
            entityType: ESelectedEntityType.specificBlock,
            entityId: this.entity().id,
        });
        this.navDrawerService.selectInNavigation({ entityId: this.entity().id });
    }

    public copyBlock(): void {
        const block = {
            ...this.entity(),
            // The copy is an ordinary block, and it must not fight the original
            // for the pinned slot.
            props: { ...this.entity().props, pin: null },
            // The copy is no longer the shared instance, but it is still a header
            // or a footer — and `meta` has to be rebuilt rather than mutated,
            // since the spread above shares it with the entity in the store.
            meta: toUnsharedBlockMeta(this.entity().meta),
        };

        this.blocksFacade.copyBlock(block, this.index() + 1);
    }

    public removeBlock() {
        if (!this.entity().meta.isShared) {
            this.blocksFacade.removeBlock(this.entity(), this.index());
            return;
        }

        const modal = this.bsModalService.show(DeleteSharedBlockModalComponent);

        modal.content.onClose.subscribe((result: boolean) => {
            if (result) {
                this.blocksFacade.removeBlock(this.entity(), this.index());
            }
        });
    }

    public readonly logoWidgetForSpecificBlock = computed<WebLegacyWidgetEntity | undefined>(
        () =>
            this.entity()
                .children?.flatMap((section) => section.children)
                ?.flatMap((column) => column.children)
                ?.filter((widget) => widget.name === Widgets.LOGO)?.[0]
    );
}
